/**
 * Tournament Rewards Tab (Campaign Variant)
 * Shows tournament prizes and rewards, reading data from the campaigns store.
 */

import React, { memo, useMemo } from 'react';

import LoadingIndicator from 'shared/components/LoadingIndicator';
import PrizeItem from './PrizeItem/PrizeItem';
import FinishedTournamentReward from './FinishedTournamentReward/FinishedTournamentReward';
import { useSelectedCampaign, TOURNAMENTS_CAMPAIGN_TYPE, type ITournamentPrize } from 'features/campaigns';
import { useTournamentLeaderboard } from '../../../../../hooks/data/useTournamentLeaderboard';
import { useAppStore } from 'store/app/appStore';

import styles from './TournamentRewardsTab.module.css';
import tableStyles from '../../../../shared/CampaignTable/CampaignTable.module.css';

interface RewardsTabProps {
  campaignId?: string;
  isActive?: boolean;
}

const areGiftSpinConfigsEqual = (
  left?: ITournamentPrize['giftSpinConfig'],
  right?: ITournamentPrize['giftSpinConfig']
) => {
  if (left === right) return true;
  if (!left || !right) return false;

  return (
    left.spinCount === right.spinCount&&
    left.bet === right.bet &&
    left.games.length === right.games.length &&
    left.games.every((game, index) => game === right.games[index])
  );
};

const RewardsTab: React.FC<RewardsTabProps> = ({ isActive = true }) => {
  const tournament = useSelectedCampaign(TOURNAMENTS_CAMPAIGN_TYPE);
  const currencyUnits = useAppStore((state) => state.balance.units || 100);

  const { data: dataLeader, isLoading } = useTournamentLeaderboard(
    tournament?.id || '',
    { enabled: isActive && !!tournament },
  );

  const isFinished = useMemo(() => {
    if (!tournament) return false;
    const now = new Date();
    const endDate = new Date(tournament.endDate);
    return now > endDate;
  }, [tournament]);

  const parsedPrizes = useMemo((): ITournamentPrize[] => {
    if (!tournament || !('prizes' in tournament) || !Array.isArray(tournament.prizes)) {
      return [];
    }
    return tournament.prizes.map((p: { prize?: string, position?: number } & ITournamentPrize) => ({
      name: p.name || p.prize || '0',
      place: p.place || p.position || 1,
      type: p.type || 'MONEY',
      currencyCode: p.currencyCode || tournament.baseCurrency || '',
      giftSpinConfig: p.giftSpinConfig
        ? {
            games: Array.isArray(p.giftSpinConfig.games) ? p.giftSpinConfig.games : [],
            spinCount: p.giftSpinConfig.spinCount ?? 0,
            bet: p.giftSpinConfig.bet != null ? p.giftSpinConfig.bet / currencyUnits : null, // Convert from credits to currency units
          }
        : undefined,
    }));
  }, [currencyUnits, tournament]);

  const playerPrize = useMemo(() => {
    if (!dataLeader || !isFinished) return null;

    // Use the player's score object directly from the API if available
    const playerPosition = dataLeader.player?.position
      ?? dataLeader.leaderboard.find((entry) => entry.isCurrentPlayer)?.position;

    if (!playerPosition) return null;
    return parsedPrizes.find((p: ITournamentPrize) => p.place === playerPosition);
  }, [dataLeader, parsedPrizes, isFinished]);

  if (!tournament || isLoading) {
    return (
      <div className={tableStyles.tabContainer}>
        <div className={styles.loaderContainer}>
          <LoadingIndicator show={true} absolute />
        </div>
      </div>
    );
  }

  const top3Prizes = parsedPrizes.length > 0 ? parsedPrizes.slice(0, 3) : [];
  const minorPrizes = parsedPrizes.length > 3 ? parsedPrizes.slice(3) : [];

  const groupAwards: ITournamentPrize[] = [];
  for (let i = 0; i < minorPrizes.length; i++) {
    const currentPrize = minorPrizes[i];
    const groupStart = i;

    while (
      i + 1 < minorPrizes.length &&
      minorPrizes[i + 1].place === minorPrizes[i].place + 1 &&
      minorPrizes[i + 1].name === currentPrize.name &&
      minorPrizes[i + 1].type === currentPrize.type &&
      areGiftSpinConfigsEqual(minorPrizes[i + 1].giftSpinConfig, currentPrize.giftSpinConfig)
    ) {
      i++;
    }

    const groupEnd = i;
    if (groupEnd > groupStart) {
      groupAwards.push({
        name: currentPrize.name,
        place: currentPrize.place,
        currencyCode: currentPrize.currencyCode,
        type: currentPrize.type,
        positionRange: `${minorPrizes[groupStart].place} - ${minorPrizes[groupEnd].place}`,
        giftSpinConfig: currentPrize.giftSpinConfig,
      });
    } else {
      groupAwards.push(currentPrize);
    }
  }

  return (
    <div className={styles.tournamentRewardsTabContainer}>
      <div
        className={styles.contentContainer}
        data-is-finished={isFinished}
      >
        {isFinished && <FinishedTournamentReward prize={playerPrize} />}
        {top3Prizes.length > 0 && (
          <div className={styles.topPrizesContainer}>
            {top3Prizes.map(({ place, type, name, currencyCode, giftSpinConfig }) => (
              <PrizeItem
                place={place}
                type={type}
                name={name}
                currencyCode={currencyCode}
                giftSpinConfig={giftSpinConfig}
                key={place}
              />
            ))}
          </div>
        )}
        {groupAwards.length > 0 &&
          <div className={styles.prizesList}>
              {groupAwards.map(({ place, type, name, currencyCode, positionRange, giftSpinConfig }) => (
                <PrizeItem
                  place={place}
                  type={type}
                  name={name}
                  currencyCode={currencyCode}
                  positionRange={positionRange}
                  giftSpinConfig={giftSpinConfig}
                  key={place}
                />
              ))}
          </div>
        }
      </div>
    </div>
  );
};

export default memo(RewardsTab);
